import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';
import { IbdEvents } from '@theme/ibd-events';
import { REVIEWS_WIDGET_DEFAULT_SETTINGS, REVIEWS_WIDGET_SCRIPT_URL } from '@theme/ibd-reviewsio-settings';
import { deepMerge } from '@theme/ibd-utilities';

/** How close (px) the widget must come to the viewport before Reviews.io loads. 200 starts the fetch just before the section is on screen; 400 pulled polaris during long Lighthouse / content-visibility layout. */
const VISIBILITY_ROOT_MARGIN_PX = 200;

/** @typedef {import('@shopify/events').ProductSelectEvent} ProductSelectEvent */
/** @typedef {import('@theme/ibd-events').IbdProductUpdateEvent} IbdProductUpdateEvent */
/** @typedef {import('@theme/ibd-reviewsio-types').ReviewsWidgetSettings} ReviewsWidgetSettings */

/**
 * IBD Product Reviews Component
 *
 * Displays Reviews.io rating snippets for products.
 *
 */
class IbdProductReviews extends Component {
  /** @type {number} */
  maxAttempts = 50;

  /** @type {number} */
  attempts = 0;

  /** @type {number|null} */
  timeout = null;

  /** @type {ReviewsWidgetSettings} */
  settings = REVIEWS_WIDGET_DEFAULT_SETTINGS;

  /** @type {IntersectionObserver|null} */
  #visibilityObserver = null;

  /** @type {boolean} Whether the widget has come near the viewport and initialisation was requested. */
  #initRequested = false;

  connectedCallback() {
    super.connectedCallback();

    this.#reset();
    this.#loadSettings();
    this.#observeVisibility();
    this.#bindEventListeners();

    if (this.hasAttribute('data-ibd-request-load')) this.requestLoad();
  }

  /**
   * Starts the Polaris fetch now. Used by the PDP star click so the bundle
   * downloads during the scroll instead of after the 200px observer fires.
   * Safe to call more than once; IntersectionObserver uses this same path.
   * @returns {void}
   */
  requestLoad() {
    if (this.#initRequested) return;

    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#initRequested = true;
    this.#injectScript();
    this.#loadReviewsSnippet();
  }

  /**
   * Defers all Reviews.io loading until the widget approaches the viewport.
   * The widget sits far below the fold, so the polaris bundle, its stylesheets,
   * icon fonts and API calls cost nothing unless the user scrolls near it.
   * The retry loop in #loadReviewsSnippet only starts counting from here, so
   * its window can't expire before the script was even requested.
   */
  #observeVisibility() {
    this.#visibilityObserver?.disconnect();

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        // Transient layouts (content-visibility sections above settling, images
        // still loading) can momentarily report the widget near the viewport.
        // Confirm against a fresh layout read before committing to the load;
        // on a stale report, re-arm the observer so it re-evaluates with
        // settled geometry.
        const rect = this.getBoundingClientRect();
        const nearViewport =
          rect.top <= window.innerHeight + VISIBILITY_ROOT_MARGIN_PX && rect.bottom >= -VISIBILITY_ROOT_MARGIN_PX;

        if (!nearViewport) {
          this.#visibilityObserver?.unobserve(this);
          this.#visibilityObserver?.observe(this);
          return;
        }

        this.requestLoad();
      },
      { rootMargin: `${VISIBILITY_ROOT_MARGIN_PX}px 0px` }
    );

    this.#visibilityObserver.observe(this);
  }

  /**
   * Called when the element is updated via morph.
   * Refreshes the skeleton reference in case it was replaced.
   */
  updatedCallback() {
    super.updatedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#reset();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#unbindEventListeners();
  }

  #loadSettings() {
    /** @type {ReviewsWidgetSettings|undefined} */
    let settings;

    try {
      const customSettings = JSON.parse(this.dataset.settings ?? '{}');
      settings = deepMerge(this.settings, customSettings);
    } catch (error) {
      console.error('IBD | Product Reviews | #loadSettings | Error parsing settings', error);
      settings = this.settings;
    }

    if (!settings || !('store' in settings) || !settings.store) {
      throw new Error('IBD | Product Reviews | #loadSettings | No store found in settings');
    }

    this.settings = settings;
  }

  #injectScript() {
    const hasScript = document.querySelector(`script[src="${REVIEWS_WIDGET_SCRIPT_URL}"]`) !== null;
    if (hasScript) {
      return;
    }

    const script = document.createElement('script');
    script.src = REVIEWS_WIDGET_SCRIPT_URL;
    script.defer = true;
    document.head.appendChild(script);
  }

  #loadReviewsSnippet() {
    this.attempts++;

    // Check for ReviewsWidget class (loaded from external script)
    // @ts-ignore - ReviewsWidget is loaded from external script
    const ReviewsWidget = window.ReviewsWidget || (typeof ReviewsWidget !== 'undefined' ? ReviewsWidget : null);

    if (typeof ReviewsWidget === 'function') {
      if (this.timeout) clearTimeout(this.timeout);
      new ReviewsWidget('#ReviewsWidget', this.settings);
    } else if (this.attempts < this.maxAttempts) {
      this.timeout = setTimeout(() => this.#loadReviewsSnippet(), 200);
    } else {
      console.error(`IBD | Product Reviews | #loadReviewsSnippet | Failed to load after ${this.maxAttempts} attempts`);
    }
  }

  #reset() {
    this.settings = REVIEWS_WIDGET_DEFAULT_SETTINGS;
    this.attempts = 0;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  /**
   * Handles a product select (variant change) event on the product page.
   * In-card variant changes are handled by product-card.js, so events
   * originating from within a product-card are ignored here.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        this.#refreshFromHtml(detail.html);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('IBD | Product Reviews | Event promise rejected:', error);
      });
  };

  /**
   * Handles an IBD product update event (combined-listing product switch).
   *
   * @param {IbdProductUpdateEvent} event - The IBD product update event.
   */
  #handleProductUpdated = (event) => {
    const html = event.detail?.data?.html;
    if (!html) return;
    this.#refreshFromHtml(html);
  };

  /**
   * Re-initialises the reviews widget from a freshly fetched document.
   *
   * @param {Document} html - The parsed HTML document with updated variant/product data.
   */
  #refreshFromHtml(html) {
    /** @type {HTMLElement|null} */
    const newElement = html.querySelector('ibd-product-reviews');
    if (!newElement) {
      console.error('IBD | Product Reviews | #refreshFromHtml | Reviews element not found in html data');
      return;
    }

    this.#loadSettings();

    // Before the widget has ever come near the viewport there is nothing to
    // re-render; the pending IntersectionObserver will initialise with the
    // freshly loaded settings when the user scrolls to it.
    if (!this.#initRequested) return;

    this.#loadReviewsSnippet();
  }

  #bindEventListeners() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.addEventListener(IbdEvents.ibdProductUpdated, this.#handleProductUpdated);
  }

  #unbindEventListeners() {
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    document.removeEventListener(IbdEvents.ibdProductUpdated, this.#handleProductUpdated);
  }
}

if (!customElements.get('ibd-product-reviews')) {
  customElements.define('ibd-product-reviews', IbdProductReviews);
}
